import { inject } from '@angular/core';
import { PAGINATION_DEFAULT } from '@core/constants';
import {
  CreationServicePayloadModel,
  CreationServiceResultModel,
  Package,
  Service,
  ServiceFiltersModel,
  ServiceResponseModel,
  UpdateServicePayloadModel,
  UpdateServiceResultModel,
} from '@core/models';
import { CompanyService, DeviceService, ServiceService } from '@core/services';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Observable, pipe, shareReplay, switchMap, tap, map } from 'rxjs';

type ServiceState = {
  isLoading: boolean;
  isLoadingService: boolean;
  isLoadingLocationServices: boolean;
  isLoadingPartnerServices: boolean;
  isLoadingPackagesByPartnerId: boolean;
  isCreatingService: boolean;
  isUpdatingService: boolean;
  serviceCreationResult?: CreationServiceResultModel;
  serviceUpdateResult?: UpdateServiceResultModel;
  services: ServiceResponseModel;
  filter: ServiceFiltersModel;
  servicesByLocationId: ServiceResponseModel;
  packagesByPartnerId?: Package[];
  servicesByPartnerId: ServiceResponseModel;
  selectedService?: Service;
};

const initialState: ServiceState = {
  isLoading: false,
  isLoadingService: false,
  isLoadingLocationServices: false,
  isLoadingPartnerServices: false,
  isLoadingPackagesByPartnerId: false,
  isCreatingService: false,
  isUpdatingService: false,
  serviceCreationResult: undefined,
  serviceUpdateResult: undefined,
  services: {
    services: [],
    numberOfItems: 0,
  },
  filter: {
    limit: PAGINATION_DEFAULT.limit,
    offset: PAGINATION_DEFAULT.offset,
  },
  servicesByLocationId: {
    services: [],
    numberOfItems: 0,
  },
  packagesByPartnerId: [],
  servicesByPartnerId: {
    services: [],
    numberOfItems: 0,
  },
  selectedService: undefined,
};

const partnerPackageCache: Record<string, Observable<Package[]>> = {};

export const ServiceStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods(
    (
      store,
      serviceService = inject(ServiceService),
      deviceService = inject(DeviceService),
      companyService = inject(CompanyService),
    ) => ({
      getServicesFilterMethod: rxMethod<ServiceFiltersModel>(
        pipe(
          tap(filter => patchState(store, { isLoading: true, filter: filter })),
          switchMap(filter => {
            return serviceService.getServices(filter).pipe(
              map(data => {
                data.services.forEach((s: Service) => {
                  s.partner = companyService.getCompanyDetail(s.partnerOid);
                });
                return data;
              }),
              tapResponse({
                next: (data: ServiceResponseModel) => {
                  addPackageData(data, serviceService);
                  addDeviceData(data, deviceService);
                  patchState(store, {
                    services: data,
                    isLoading: false,
                  });
                },
                error: () => {
                  patchState(store, { isLoading: false });
                },
              }),
            );
          }),
        ),
      ),

      getServicesByLocationId: rxMethod<number>(
        pipe(
          tap(() => patchState(store, { isLoadingLocationServices: true })),
          switchMap(locationId => {
            return serviceService.getServicesByLocationId(locationId).pipe(
              map(data => {
                data.services.forEach((s: Service) => {
                  s.partner = companyService.getCompanyDetail(s.partnerOid);
                });
                return data;
              }),
              tapResponse({
                next: (data: ServiceResponseModel) => {
                  addPackageData(data, serviceService);
                  addDeviceData(data, deviceService);
                  patchState(store, {
                    servicesByLocationId: data,
                    isLoadingLocationServices: false,
                  });
                },
                error: () => {
                  patchState(store, { isLoadingLocationServices: false });
                },
              }),
            );
          }),
        ),
      ),
      getServicesByPartnerOid: rxMethod<
        { partnerOid: string; limit?: number; offset?: number } | string
      >(
        pipe(
          tap(() => patchState(store, { isLoadingPartnerServices: true })),
          switchMap(params => {
            const partnerOid = typeof params === 'string' ? params : params.partnerOid;
            const limit = typeof params === 'string' ? 10 : (params.limit ?? 10);
            const offset = typeof params === 'string' ? 0 : (params.offset ?? 0);
            return serviceService.getServicesByPartnerOid(partnerOid, limit, offset).pipe(
              map(data => {
                data.services.forEach((s: Service) => {
                  s.partner = companyService.getCompanyDetail(s.partnerOid);
                });
                return data;
              }),
              tapResponse({
                next: (data: ServiceResponseModel) => {
                  addPackageData(data, serviceService);
                  addDeviceData(data, deviceService);
                  patchState(store, {
                    servicesByPartnerId: data,
                    isLoadingPartnerServices: false,
                  });
                },
                error: () => {
                  patchState(store, { isLoadingPartnerServices: false });
                },
              }),
            );
          }),
        ),
      ),
      getPackagesByPartnerOid: rxMethod<string>(
        pipe(
          tap(partnerOid => {
            let partnerPackages = partnerPackageCache[partnerOid];
            if (partnerPackages === undefined) {
              patchState(store, { isLoadingPackagesByPartnerId: true });
              partnerPackages = serviceService.getPackagesByPartnerOid(partnerOid, 1000).pipe(
                map(ppr => ppr.packages),
                shareReplay(1),
                tap(() => patchState(store, { isLoadingPackagesByPartnerId: false })),
              );

              partnerPackageCache[partnerOid] = partnerPackages;
            }
            partnerPackages.subscribe(packages => {
              patchState(store, { packagesByPartnerId: packages });
            });
          }),
        ),
      ),
      createService: rxMethod<CreationServicePayloadModel>(
        pipe(
          switchMap(serviceCreate => {
            patchState(store, { isCreatingService: true, serviceCreationResult: undefined });
            return serviceService.createService(serviceCreate).pipe(
              tapResponse({
                next: result => {
                  patchState(store, { isCreatingService: false, serviceCreationResult: result });
                },
                error: result => {
                  patchState(store, {
                    isCreatingService: false,
                    serviceCreationResult: result as any,
                  });
                },
              }),
            );
          }),
        ),
      ),
      updateService: rxMethod<{ service: UpdateServicePayloadModel; serviceId: number }>(
        pipe(
          switchMap(serviceUpdate => {
            patchState(store, { isUpdatingService: true, serviceUpdateResult: undefined });
            return serviceService
              .updateService(serviceUpdate.serviceId, serviceUpdate.service)
              .pipe(
                tapResponse({
                  next: result => {
                    patchState(store, { isUpdatingService: false, serviceUpdateResult: result });
                  },
                  error: result => {
                    patchState(store, {
                      isUpdatingService: false,
                      serviceUpdateResult: result as any,
                    });
                  },
                }),
              );
          }),
        ),
      ),
      activateService: rxMethod<number>(
        pipe(
          switchMap(serviceId => {
            patchState(store, { isUpdatingService: true, serviceUpdateResult: undefined });
            return serviceService.activateService(serviceId).pipe(
              tapResponse({
                next: result => {
                  patchState(store, { isUpdatingService: false, serviceUpdateResult: result });
                },
                error: result => {
                  patchState(store, {
                    isUpdatingService: false,
                    serviceUpdateResult: result as any,
                  });
                },
              }),
            );
          }),
        ),
      ),
      setSelectedService(service: Service): void {
        patchState(store, { selectedService: service });
      },
      deactivateService: rxMethod<{ serviceId: number; deactivationDate: string }>(
        pipe(
          switchMap(payload => {
            patchState(store, { isUpdatingService: true, serviceUpdateResult: undefined });
            return serviceService
              .deactivateService(payload.serviceId, payload.deactivationDate)
              .pipe(
                tapResponse({
                  next: result => {
                    patchState(store, { isUpdatingService: false, serviceUpdateResult: result });
                  },
                  error: result => {
                    patchState(store, {
                      isUpdatingService: false,
                      serviceUpdateResult: result as any,
                    });
                  },
                }),
              );
          }),
        ),
      ),
    }),
  ),
);
function addPackageData(data: ServiceResponseModel, serviceService: ServiceService) {
  data.services.forEach(s => {
    let partnerPackages = partnerPackageCache[s.partnerOid];
    if (partnerPackages === undefined) {
      partnerPackages = serviceService.getPackagesByPartnerOid(s.partnerOid, 1000).pipe(
        map(ppr => ppr.packages),
        shareReplay(1),
      );
      partnerPackageCache[s.partnerOid] = partnerPackages;
    }
    let servicePackage = partnerPackages.pipe(map(pp => pp.find(p => p.packageId == s.packageId)));
    s.package = servicePackage;
  });
}
function addDeviceData(data: ServiceResponseModel, deviceService: DeviceService) {
  data.services
    .filter(s => s.deviceId)
    .forEach(s => {
      s.device = deviceService.getDeviceDetail(s.deviceId!.toString());
    });
}
